<template>
  <div class="template-mng" :class="{ 'is-embedded': embedded }">
    <div class="page-header">
      <h2 class="page-title">爆款模板</h2>
      <el-input
          v-model="searchKeyword"
          placeholder="搜索模板名称或备注"
          clearable
          size="large"
          style="width: 380px"
        >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <div class="header-actions">
        <el-button type="primary" @click="handleAddTemplate">
          <el-icon><Plus /></el-icon>
          新增模板
        </el-button>
      </div>
    </div>

    <div class="template-list">
      <div
        v-for="template in filteredTemplateList"
        :key="template.id"
        class="template-item"
      >
        <div class="template-header">
          <div class="template-header-right">
            <div class="template-header-left">
              <h4 class="template-name">
                {{ template.name }}
                <el-text type="info" size="small">（{{ template.remark || '无说明' }}）</el-text>
                <el-tag v-if="template.digitalHumanVoice" type="success" size="small" style="margin-left: 8px;">数字人口播</el-tag>
                <el-text type="info" size="small" style="margin-left: 8px;">创建于：{{ template.createDate }}</el-text>
              </h4>
            </div>
            <div class="template-actions">
              <el-button text type="primary" size="small" @click="handleEditTemplate(template)">
                <el-icon><EditPen /></el-icon>修改
              </el-button>
              <el-button text type="danger" size="small" @click="handleDeleteTemplate(template.id)">
                <el-icon><Delete /></el-icon>删除
              </el-button>
            </div>
          </div>
        </div>
        <div class="template-content">
          <div class="template-shots-section">
            <div class="shots-list">
              <div
                v-for="(shot, shotIndex) in (expandedTemplateId === template.id ? template.shots : template.shots.slice(0, shotsPerRow))"
                :key="shotIndex"
                class="shot-view-item-inline"
              >
                <div class="shot-item-header-inline">
                  <el-tooltip v-if="shot.materialExample && shot.materialExample.prompt" :content="shot.materialExample.prompt" placement="top" effect="dark">
                    <span>{{ shotIndex + 1 }}：{{ shot.name || '-' }}</span>
                  </el-tooltip>
                  <span v-else>{{ shotIndex + 1 }}：{{ shot.name || '-' }}</span>
                </div>
                <div class="shot-info-list">
                  <div class="material-example-container">
                    <el-image
                      v-if="shot.materialExample && shot.materialExample.thumbnail"
                      :src="shot.materialExample.thumbnail"
                      :alt="shot.materialExample.fileName"
                      fit="cover"
                      class="material-example-image"
                      :preview-src-list="[shot.materialExample.thumbnail]"
                    />
                    <video
                      v-else-if="shot.materialExample && getMaterialExampleVideoUrl(shot.materialExample)"
                      :src="getMaterialExampleVideoUrl(shot.materialExample)"
                      class="material-example-video"
                      muted
                      loop
                      playsinline
                    />
                    <div v-else class="material-example-placeholder">
                      <el-icon :size="20"><VideoCamera /></el-icon>
                    </div>
                  </div>
                  <div class="shot-info-item">
                    <el-tag type="warning" size="small">时长：{{ shot.minDuration }}s - {{ shot.maxDuration }}s</el-tag>
                    <el-tag type="success" size="small">方式：{{ shot.insertMode === 'insert' ? '插入' : shot.insertMode === 'overwrite' ? '覆盖' : '主角口播' }}</el-tag>
                    <el-tag :type="shot.hasPiP ? 'primary' : 'info'" size="small">画中画：{{ shot.hasPiP ? '是' : '否' }}</el-tag>
                  </div>
                </div>
              </div>
              <!-- 显示更多提示 -->
              <div
                v-if="template.shots.length > shotsPerRow && expandedTemplateId !== template.id"
                class="shot-more-hint"
                @click.stop="expandedTemplateId = template.id"
              >
                <el-icon :size="20"><More /></el-icon>
                <span>还有 {{ template.shots.length - shotsPerRow }} 个镜头</span>
              </div>
              <!-- 收起提示 -->
              <div
                v-if="template.shots.length > shotsPerRow && expandedTemplateId === template.id"
                class="shot-more-hint"
                @click.stop="expandedTemplateId = null"
              >
                <el-icon :size="20"><ArrowUp /></el-icon>
                <span>收起</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="filteredTemplateList.length === 0" class="empty-template-list">
        <el-text type="info">暂无模板，请点击「新增模板」添加</el-text>
      </div>
    </div>

    <!-- 新增/编辑模板对话框 -->
    <el-dialog
      v-model="templateDialogVisible"
      :title="editingTemplate ? '编辑模板' : '新增模板'"
      width="800px"
      :close-on-click-modal="false"
      class="template-edit-dialog"
      append-to-body
      @closed="handleTemplateDialogClosed"
    >
      <el-form
        ref="templateFormRef"
        :model="templateForm"
        label-width="90px"
        label-position="left"
      >
        <el-form-item label="模板名称" prop="name">
          <el-input
            v-model="templateForm.name"
            placeholder="请输入模板名称"
            maxlength="60"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="备注信息" prop="remark">
          <el-input
            v-model="templateForm.remark"
            placeholder="请输入备注"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="数字人口播" prop="digitalHumanVoice">
          <el-switch v-model="templateForm.digitalHumanVoice" />
        </el-form-item>

        <el-form-item label="镜头序列">
          <div class="shots-edit-container">
            <div style="display: flex; justify-content: flex-start;">
                <el-button type="primary" @click="handleAddShot" class="add-shot-btn">
                <el-icon><Plus /></el-icon>
                添加镜头
                </el-button>
                <el-button type="success" @click="openParseVideoDialog" class="add-shot-btn">
                <el-icon><VideoCamera /></el-icon>
                解析视频
                </el-button>
                <div class="power-text">
                  <el-text type="info">解析视频消耗算力：</el-text>
                  <el-icon color="#909399">
                    <Coin />
                  </el-icon>
                  <el-text type="info">1200 / 30秒</el-text>

                </div>
            </div>

            <div ref="shotsListRef" class="shots-edit-list">
              <div
                v-for="(shot, index) in templateForm.shots"
                :key="`shot-${shot.id || index}`"
                class="shot-view-item"
              >
                <div class="shot-item-header">
                  <div class="shot-drag-handle">
                    <el-icon :size="16"><Rank /></el-icon>
                  </div>
                  <el-tooltip :content="`${index + 1}：${shot.name || '-'}`" placement="top" :show-after="300">
                    <span class="shot-name-text">{{ index + 1 }}：{{ shot.name || '-' }}</span>
                  </el-tooltip>
                  <el-button text type="primary" size="small" circle @click="handleEditShot(index)">
                    <el-icon><EditPen /></el-icon>
                  </el-button>
                  <el-button text type="danger" size="small" circle @click="handleRemoveShot(index)">
                    <el-icon><Delete /></el-icon>
                  </el-button>
                </div>
                <div class="shot-info-list">
                  <div class="material-example-container">
                    <el-image
                      v-if="shot.materialExample && shot.materialExample.thumbnail"
                      :src="shot.materialExample.thumbnail"
                      :alt="shot.materialExample.fileName"
                      fit="cover"
                      class="material-example-image"
                      :preview-src-list="[shot.materialExample.thumbnail]"
                    />
                    <video
                      v-else-if="shot.materialExample && getMaterialExampleVideoUrl(shot.materialExample)"
                      :src="getMaterialExampleVideoUrl(shot.materialExample)"
                      class="material-example-video"
                      muted
                      loop
                      playsinline
                    />
                    <div v-else class="material-example-placeholder">
                      <el-icon :size="20"><VideoCamera /></el-icon>
                    </div>
                  </div>
                  <div class="shot-info-item">
                    <el-tag type="warning" size="small">时长：{{ shot.minDuration }}s - {{ shot.maxDuration }}s</el-tag>
                    <el-tag type="success" size="small">方式：{{ shot.insertMode === 'insert' ? '插入' : shot.insertMode === 'overwrite' ? '覆盖' : '口播' }}</el-tag>
                    <el-tag :type="shot.hasPiP ? 'primary' : 'info'" size="small">画中画：{{ shot.hasPiP ? '是' : '否' }}</el-tag>
                  </div>
                </div>
              </div>
              <div v-if="templateForm.shots.length === 0" class="empty-shots-hint">
                <el-text type="info">暂无镜头，请点击上方按钮添加</el-text>
              </div>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="templateDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSaveTemplate" :loading="saving">
            保存
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 解析视频弹窗：上传视频后解析并生成镜头序列 -->
    <el-dialog
      v-model="parseVideoDialogVisible"
      title="解析视频构建镜头序列"
      width="640px"
      :close-on-click-modal="false"
      class="parse-video-dialog"
      append-to-body
      @closed="handleParseVideoDialogClosed"
    >
      <Upload
        v-model="parseVideoUrl"
        accept=".mp4,.avi,.mkv,.wmv"
        upload-tip="mp4/avi/mkv/wmv 文件，5G以内"
        cloud-selector-tip="从云盘空间中选择mp4/avi/mkv/wmv文件"
        :max-size="5000"
        :enable-cloud-select="true"
        storage-key="TemplateMngParseVideo"
        ref="parseVideoUploadRef"
        @file-name-change="handleParseVideoFileNameChange"
      >
        <template #preview="{ fileUrl }">
          <div class="video-preview-tab">
            <div class="video-preview-relative">
              <video :src="fileUrl" controls style="width:100%; height: 400px;" @loadedmetadata="onVideoLoadedMetadata"/>
            </div>
            <div style="margin:12px 0;text-align: center;display: flex; justify-content: space-between;">
              <el-button size="small" @click="clearParseVideo">重新上传</el-button>
              <div class="power-text">
                <el-text type="info">消耗算力：</el-text>
                <el-icon color="#909399"><Coin /></el-icon>
                <el-text>{{ powerVideo }}</el-text>
                <el-tooltip content="根据上传视频时长计算，每30秒1200算力" placement="top">
                  <el-icon style="margin-left: 8px; cursor: help; color: #909399;"><QuestionFilled /></el-icon>
                </el-tooltip>
              </div>
              <el-button type="primary" size="small" @click="startVideoParseForTemplate(fileUrl)" :loading="parseVideoLoading">
                解析视频内容
              </el-button>

            </div>
          </div>
        </template>
      </Upload>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="parseVideoDialogVisible = false">取消</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 添加/修改镜头对话框 -->
    <el-dialog
      v-model="shotDialogVisible"
      :title="editingShotIndex >= 0 ? '修改镜头' : '添加镜头'"
      width="600px"
      :close-on-click-modal="false"
      class="shot-edit-dialog"
      append-to-body
    >
      <el-form
        ref="shotFormRef"
        :model="shotForm"
        label-width="100px"
        label-position="left"
      >
        <el-form-item label="镜头名称" prop="name">
          <el-input
            v-model="shotForm.name"
            placeholder="请输入镜头名称"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="最短时长" prop="minDuration">
          <el-input-number
            v-model="shotForm.minDuration"
            :min="0"
            :max="999"
            style="width: 180px"
          >
            <template #suffix><span>秒</span></template>
          </el-input-number>
        </el-form-item>
        <el-form-item label="最大时长" prop="maxDuration">
          <el-input-number
            v-model="shotForm.maxDuration"
            :min="0"
            :max="999"
            style="width: 180px"
          >
            <template #suffix><span>秒</span></template>
          </el-input-number>
        </el-form-item>
        <el-form-item label="镜头模式" prop="insertMode">
          <el-radio-group v-model="shotForm.insertMode">
            <el-radio-button label="insert">片段插入</el-radio-button>
            <el-radio-button label="narration" :disabled="!templateForm.digitalHumanVoice">主角口播</el-radio-button>
            <el-radio-button label="overwrite" :disabled="!templateForm.digitalHumanVoice">片段覆盖</el-radio-button>
          </el-radio-group>

          <div v-if="shotForm.insertMode === 'overwrite'" style="margin-left: 20px; display: flex; align-items: center;">
            <el-text type="info" size="small">画中画：</el-text>
            <el-switch v-model="shotForm.hasPiP" />
          </div>

          <div style="margin-left: 10px;">
            <el-popover
              width="380"
              title="模式说明"
              placement="right"
            >
              <template #reference>
                <el-icon><QuestionFilled /></el-icon>
              </template>
              <template #default>
                <span>片段插入</span><br/>
                <span>中断基底视频，插入素材，保留素材的声音和图像；</span><br/><br/>
                <span>主角口播（需要打开“数字人口播”开关才能选择）</span><br/>
                <span>画面只使用数字人口播视频和语音，不需要添加素材；</span><br/><br/>
                <span>片段覆盖（需要打开“数字人口播”开关才能选择）</span><br/>
                <span>将素材静音保留画面，使用预设的口播语音（如有数字人视频，则使用其的语音）。</span><br/><br/>
                <span>画中画（需要选择 片段覆盖 模式）</span><br/>
                <span>在片段覆盖的基础上，右下角增加数字人口播画中画。</span>
              </template>
            </el-popover>

          </div>

        </el-form-item>
        <el-form-item label="示例提示词" prop="prompt">
          <el-input
            v-model="shotForm.prompt"
            :rows="4"
            type="textarea"
            placeholder="输入镜头提示词"
            />
        </el-form-item>
        <el-form-item label="素材示例" prop="materialExample">
          <div class="material-example-selector">
            <div class="material-example-preview" @click="handleSelectMaterialExample">
              <div v-if="!shotForm.materialExample" class="material-example-empty">
                <el-icon :size="32"><Plus /></el-icon>
              </div>
              <div v-else class="material-example-thumbnail">
                <video
                  v-if="getMaterialExampleVideoUrl(shotForm.materialExample)"
                  :src="getMaterialExampleVideoUrl(shotForm.materialExample)"
                  class="thumbnail-video"
                  controls
                  @click.stop
                />
                <img
                  v-else-if="shotForm.materialExample && shotForm.materialExample.thumbnail"
                  :src="shotForm.materialExample.thumbnail"
                  :alt="shotForm.materialExample.fileName"
                  class="thumbnail-image"
                />
                <div v-else class="thumbnail-placeholder">
                  <el-icon :size="24"><VideoCamera /></el-icon>
                </div>
                <div
                  v-if="!getMaterialExampleVideoUrl(shotForm.materialExample)"
                  class="thumbnail-overlay"
                >
                  <el-icon :size="20"><VideoPlay /></el-icon>
                </div>
              </div>
              <el-button
                v-if="shotForm.materialExample"
                circle
                type="danger"
                size="small"
                :icon="Delete"
                @click.stop="shotForm.materialExample = null"
                class="clear-material-btn"
              />
            </div>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="shotDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleConfirmShot">确定</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 视频选择对话框 -->
    <el-dialog
      v-model="videoSelectorDialogVisible"
      title="选择素材示例视频"
      width="900px"
      :close-on-click-modal="false"
      class="video-selector-dialog"
      append-to-body
    >
      <el-tabs v-model="videoSelectorTab" class="video-selector-tabs" @tab-change="handleVideoTabChange">
        <el-tab-pane label="选择已有视频" name="select">
          <div class="video-selector-content">
            <div class="video-search-section">
              <el-input
                v-model="videoSearchKeyword"
                placeholder="根据视频标签模糊搜索"
                clearable
                style="width: 100%"
              >
                <template #prefix><el-icon><Search /></el-icon></template>
              </el-input>
            </div>
            <div class="video-list-section">
              <div v-if="videoListLoading" class="video-loading">
                <el-icon class="is-loading"><Loading /></el-icon>
                <span>加载中...</span>
              </div>
              <div v-else-if="filteredVideoList.length === 0" class="empty-video-list">
                <el-text type="info">暂无视频</el-text>
              </div>
              <div v-else class="video-selector-grid">
                <div
                  v-for="video in filteredVideoList"
                  :key="video.id"
                  class="video-selector-item"
                  :class="{ 'video-selector-item-selected': selectedVideoId === video.id }"
                  @click="handleSelectVideo(video)"
                >
                  <div class="video-selector-thumbnail">
                    <div class="thumbnail-icon" v-if="!video.thumbnail">
                      <el-icon :size="32"><VideoCamera /></el-icon>
                    </div>
                    <img v-else :src="video.thumbnail" :alt="video.fileName" class="thumbnail-image" />
                    <div class="thumbnail-play-button">
                      <el-icon :size="24" color="#fff"><VideoPlay /></el-icon>
                    </div>
                  </div>
                  <div class="video-selector-info">
                    <div class="video-selector-name" :title="video.fileName">{{ video.fileName }}</div>
                    <div v-if="video.tags && video.tags.length > 0" class="video-selector-tags">
                      <el-tag v-for="(tag, index) in video.tags" :key="index" size="small" type="info" style="margin-right: 4px;">{{ tag }}</el-tag>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>
        <el-tab-pane label="上传新视频" name="upload">
          <div class="video-upload-section">
            <Upload
              ref="uploadRef"
              v-model="uploadedVideoUrl"
              accept=".mp4"
              upload-tip="支持 mp4 格式视频，5G以内"
              :show-preview="true"
              @file-name-change="handleUploadedVideoChange"
            />
          </div>
        </el-tab-pane>
      </el-tabs>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="handleVideoDialogClose">取消</el-button>
          <el-button
            type="primary"
            @click="handleConfirmVideoSelection"
            :disabled="!selectedVideoId && !uploadedVideoUrl"
          >
            确定
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { EditPen, Search, Plus, Delete, VideoCamera, VideoPlay, Loading, Coin, QuestionFilled, Picture, More, ArrowUp } from '@element-plus/icons-vue'
import Sortable from 'sortablejs'
import Upload from '@/apps/portal/components/Upload.vue'
import { getTemplateList, saveTemplate, updateTemplate, deleteTemplate } from '@/api/portal/pm'
import { parseTemplateVideo } from '@/api/portal/ai'
import { calculatePower } from '@/api/portal/aitools'
import { getPathWithAgent } from '@/apps/portal/utils/agentUtils'

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
})

const router = useRouter()

function isLoggedIn() {
  return !!localStorage.getItem('token')
}

function redirectToLogin(message = '请先登录后再使用视频模板功能') {
  ElMessage.warning(message)
  router.push(getPathWithAgent('/login'))
}

const searchKeyword = ref('')
const templateList = ref([])
const loading = ref(false)
const saving = ref(false)
const expandedTemplateId = ref(null)
const shotsPerRow = ref(6) // 每行显示的镜头数量


const templateDialogVisible = ref(false)
const templateFormRef = ref(null)
const editingTemplate = ref(null)
const templateForm = ref({
  id: null,
  name: '',
  remark: '',
  digitalHumanVoice: true,
  coverImage: null,
  shots: []
})
const shotsListRef = ref(null)
let sortableInstance = null

// 解析视频弹窗
const parseVideoDialogVisible = ref(false)
const parseVideoUrl = ref('')
const parseVideoLoading = ref(false)
const parseVideoUploadRef = ref(null)
const powerVideo = ref(1200)

const shotDialogVisible = ref(false)
const editingShotIndex = ref(-1) // -1 表示添加，>=0 表示编辑对应下标
const shotFormRef = ref(null)
const shotForm = ref({
  id: null,
  name: '',
  materialExample: null,
  minDuration: 3,
  maxDuration: 5,
  hasPiP: false,
  insertMode: 'insert'
})

watch(() => shotForm.value.insertMode, (newMode) => {
  if (newMode !== 'overwrite') {
    shotForm.value.hasPiP = false
  }
})

const videoSelectorDialogVisible = ref(false)
const videoSelectorTab = ref('select')
const videoSearchKeyword = ref('')
const videoListLoading = ref(false)
const videoList = ref([])
const selectedVideoId = ref(null)
const tempSelectedVideo = ref(null)
const uploadedVideoUrl = ref('')
const uploadedVideoFileName = ref('')
const uploadRef = ref(null)

// 封面上传相关
const coverUploadRef = ref(null)

const filteredTemplateList = computed(() => {
  if (!searchKeyword.value || !searchKeyword.value.trim()) return templateList.value
  const keyword = searchKeyword.value.toLowerCase().trim()
  return templateList.value.filter(t => {
    const name = (t.name || '').toLowerCase()
    const remark = (t.remark || '').toLowerCase()
    return name.includes(keyword) || remark.includes(keyword)
  })
})

const filteredVideoList = computed(() => {
  if (!videoSearchKeyword.value) return videoList.value
  const keyword = videoSearchKeyword.value.toLowerCase()
  return videoList.value.filter(video => {
    if (video.tags && Array.isArray(video.tags)) {
      return video.tags.some(tag => tag && tag.toLowerCase().includes(keyword))
    }
    if (video.fileName) {
      return video.fileName.toLowerCase().includes(keyword)
    }
    return false
  })
})

// 动态算力计算 分析视频
const calculateDynamicPowerForVideo = async (duration) => {
  try {

    let actualUsage = 1 // 默认值
    if (duration > 0) {
        try {
          actualUsage =  Math.ceil(duration) // 向上取整到秒
          const res = await calculatePower(8, actualUsage) //与08工具算力一致
          if (res && res.code === 0 && res.data) {
            powerVideo.value = res.data*4 || powerVideo.value//算力翻4倍
          }
        } catch (e) {
          console.error('计算算力失败:', e)
        }
    } else {
      // 没有视频时使用默认值
      const res = await calculatePower(8, actualUsage)
      if (res && res.code === 0 && res.data) {
        powerVideo.value = res.data*4 || powerVideo.value//算力翻4倍
      }
    }


  } catch (e) {
    console.error('计算算力失败:', e)
  }
}

async function loadTemplateList() {
  loading.value = true
  try {
    const res = await getTemplateList('video_production')
    //console.log('加载模板列表响应:', res)
    if (res && res.code === 0) {
      templateList.value = (res.list || []).map(template => {
        //console.log('处理模板:', template.templateName, '原始templateContent:', template.templateContent)
        let templateContent = {}
        if (template.templateContent) {
          try {
            templateContent = JSON.parse(template.templateContent)
            //console.log('解析后的templateContent:', templateContent)
          } catch (e) {
            console.error('解析模板内容失败:', e)
          }
        }
        const result = {
          id: template.id,
          name: template.templateName,
          remark: template.templateDescription || '',
          createDate:template.createDate || '',
          digitalHumanVoice: templateContent.digitalHumanVoice,
          coverImage: template.coverImage || null,
          shots: templateContent.shots || []
        }
        //console.log('转换后的模板数据:', result)
        return result
      })
      //console.log('最终templateList:', templateList.value)
    }
  } catch (error) {
    console.error('加载模板列表失败:', error)
    ElMessage.error('加载模板列表失败')
  } finally {
    loading.value = false
  }
}

function handleAddTemplate() {
  if (!isLoggedIn()) {
    redirectToLogin()
    return
  }
  editingTemplate.value = null
  templateForm.value = {
    id: null,
    name: '',
    remark: '',
    digitalHumanVoice: true,
    coverImage: null,
    shots: []
  }
  templateDialogVisible.value = true
  nextTick(() => initSortable())
}

function handleEditTemplate(template) {
  editingTemplate.value = template
  //console.log('编辑模板，原始数据:', template)
  const shots = (template.shots || []).map((shot, index) => {
    //console.log(`处理镜头 ${index + 1}:`, shot)
    return {
      ...shot,
      id: shot.id || Date.now() + index,
      // 保留 materialExample，不要设置为 null
      materialExample: shot.materialExample,
      materialTag: undefined
    }
  })
  //console.log('处理后的镜头数据:', shots)
  templateForm.value = {
    id: template.id,
    name: template.name,
    remark: template.remark || '',
    digitalHumanVoice: template.digitalHumanVoice,
    coverImage: template.coverImage || null,
    shots: shots
  }
  templateDialogVisible.value = true
  nextTick(() => initSortable())
}

async function handleDeleteTemplate(templateId) {
  try {
    await ElMessageBox.confirm('确定要删除该模板吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    const res = await deleteTemplate(templateId)
    if (res && res.code === 0) {
      templateList.value = templateList.value.filter(t => t.id !== templateId)
      ElMessage.success('模板删除成功')
    } else {
      ElMessage.error(res.msg || '模板删除失败')
    }
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      console.error('删除模板失败:', error)
      ElMessage.error('删除模板失败')
    }
  }
}

function handleTemplateDialogClosed() {
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }
}

async function handleSaveTemplate() {
  if (!templateForm.value.name) {
    ElMessage.warning('请输入模板名称')
    return
  }
  if (templateForm.value.shots.length === 0) {
    ElMessage.warning('请至少添加一个镜头')
    return
  }
  //console.log('保存前，templateForm.shots:', templateForm.value.shots)
  console.log('保存前，templateForm.value.digitalHumanVoice:', templateForm.value.digitalHumanVoice)
  templateForm.value.digitalHumanVoice
  saving.value = true
  try {
    const templateContent = {
      digitalHumanVoice: templateForm.value.digitalHumanVoice,
      shots: JSON.parse(JSON.stringify(templateForm.value.shots))
    }
    //console.log('保存前，templateContent:', templateContent)
    const templateData = {
      templateName: templateForm.value.name,
      templateType: 'video_production',
      templateDescription: templateForm.value.remark || '',
      templateContent: JSON.stringify(templateContent),
      coverImage: templateForm.value.coverImage || null
    }
    console.log('保存前，templateContent字符串:', templateData.templateContent)

    // 验证 templateContent 是否包含 shots 数据
    if (!templateData.templateContent || templateData.templateContent === '{}' ||
        !templateData.templateContent.includes('"shots"')) {
      ElMessage.error('镜头数据序列化失败，请重试')
      saving.value = false
      return
    }

    let res
    if (editingTemplate.value && templateForm.value.id) {
      templateData.id = templateForm.value.id
      //('更新模板，发送数据:', templateData)
      //console.log('发送数据的所有字段:', Object.keys(templateData))
      res = await updateTemplate(templateData)
    } else {
      //console.log('新增模板，发送数据:', templateData)
      res = await saveTemplate(templateData)
    }
    //console.log('保存响应:', res)
    if (res && res.code === 0) {
      ElMessage.success(editingTemplate.value ? '模板更新成功' : '模板添加成功')
      templateDialogVisible.value = false
      await loadTemplateList()

      // 验证保存后的数据
      const savedTemplate = templateList.value.find(t => t.id === templateForm.value.id)
      if (savedTemplate && (!savedTemplate.shots || savedTemplate.shots.length === 0)) {
        console.error('警告：模板保存后镜头数据丢失！')
        console.error('保存前的镜头数:', templateForm.value.shots.length)
        console.error('保存后的镜头数:', savedTemplate.shots ? savedTemplate.shots.length : 0)
        ElMessage.warning('警告：模板已保存，但镜头数据可能未正确保存，请检查后端接口')
      }
    } else {
      ElMessage.error(res.msg || '保存失败')
    }
  } catch (error) {
    console.error('保存模板失败:', error)
    ElMessage.error('保存模板失败')
  } finally {
    saving.value = false
  }
}

function handleAddShot() {
  editingShotIndex.value = -1
  shotForm.value = {
    id: null,
    name: '',
    prompt: '',
    materialExample: null,
    minDuration: 3,
    maxDuration: 5,
    hasPiP: false,
    insertMode: 'insert'
  }
  shotDialogVisible.value = true
}

function handleEditShot(index) {
  const shot = templateForm.value.shots[index]
  if (!shot) return
  editingShotIndex.value = index
  shotForm.value = {
    id: shot.id ?? null,
    name: shot.name || '',
    prompt: (shot.materialExample && shot.materialExample.prompt) || '',
    materialExample: shot.materialExample ? JSON.parse(JSON.stringify(shot.materialExample)) : null,
    minDuration: shot.minDuration ?? 3,
    maxDuration: shot.maxDuration ?? 5,
    hasPiP: shot.hasPiP ?? false,
    insertMode: shot.insertMode || 'insert'
  }
  shotDialogVisible.value = true
}

// 打开解析视频弹窗
function openParseVideoDialog() {
  parseVideoUrl.value = ''
  if (parseVideoUploadRef.value && parseVideoUploadRef.value.clearFile) {
    parseVideoUploadRef.value.clearFile()
  }
  parseVideoDialogVisible.value = true
}

// 解析视频弹窗关闭时清空
function handleParseVideoDialogClosed() {
  parseVideoUrl.value = ''
  if (parseVideoUploadRef.value && parseVideoUploadRef.value.clearFile) {
    parseVideoUploadRef.value.clearFile()
  }
}

// 重新上传（清空当前视频）
function clearParseVideo() {
  parseVideoUrl.value = ''
  if (parseVideoUploadRef.value && parseVideoUploadRef.value.clearFile) {
    parseVideoUploadRef.value.clearFile()
  }
}

// 视频加载完成时计算算力
const onVideoLoadedMetadata = (event) => {
  //console.log('视频加载完成，时长:', event.target.duration, '秒')
  calculateDynamicPowerForVideo(event.target.duration)
}

function handleParseVideoFileNameChange() {
  // 可选：上传完成后文件名变化时的处理
}

/** 从 geminiVideoParse 响应中解析 payload（兼容 R.ok(map) 与 R.ok().put("data", map)） */
function resolveVideoParsePayload(response) {
  if (!response) return null
  if (response.data && typeof response.data === 'object' && ('result' in response.data || 'status' in response.data)) {
    return response.data
  }
  return response
}

// 解析视频并构建镜头序列（参考 D07Edit startVideoParse + analysis_result 结构）
async function startVideoParseForTemplate(fileUrl) {
  if (!fileUrl) {
    ElMessage.warning('请先上传视频')
    return
  }
  parseVideoLoading.value = true
  try {
    const response = await parseTemplateVideo(fileUrl)
    console.log('视频解析响应:', response)

    const payload = resolveVideoParsePayload(response)
    const status = payload?.status
    const result = payload?.result
    const topLevelError = payload?.error

    // Python 解析异常时 status=failed、result=null，但 Java 仍返回 code=0
    if (status === 'failed' || (!result && topLevelError)) {
      ElMessage.error('视频解析失败: ' + (topLevelError || '未知错误'))
      return
    }

    if (!result) {
      console.error('视频解析返回数据结构异常:', response)
      ElMessage.error('视频解析失败，返回数据格式错误')
      return
    }

    if (result.error_info) {
      const errMsg = typeof result.error_info === 'object'
        ? (result.error_info.error || result.error_info.message || JSON.stringify(result.error_info))
        : String(result.error_info)
      ElMessage.error('视频解析失败: ' + (errMsg || '未知错误'))
      return
    }

    if (result.analysis_result && Array.isArray(result.analysis_result) && result.analysis_result.length > 0) {
      const shots = buildShotsFromAnalysisResult(result.analysis_result)
      templateForm.value.shots.push(...shots)
      ElMessage.success(`已从视频解析出 ${shots.length} 个镜头，已加入当前模板`)
      parseVideoDialogVisible.value = false
      parseVideoUrl.value = ''
      if (parseVideoUploadRef.value && parseVideoUploadRef.value.clearFile) {
        parseVideoUploadRef.value.clearFile()
      }
      nextTick(() => initSortable())
    } else {
      ElMessage.warning('视频解析完成，但未识别到镜头信息')
    }
  } catch (error) {
    console.error('视频解析失败:', error)
    ElMessage.error('视频解析失败: ' + (error.message || '未知错误'))
  } finally {
    parseVideoLoading.value = false
  }
}

// 将 API 返回的 analysis_result 转为模板镜头序列（与 TasksTab 中 shot 结构一致）
function buildShotsFromAnalysisResult(analysisResult) {
  return analysisResult.map((shot, i) => {
    const startTime = Number(shot.start_time) || 0
    const endTime = Number(shot.end_time) || startTime + 2
    const duration = endTime > startTime ? Number((endTime - startTime).toFixed(2)) : 1
    const clampedDuration = Math.max(0.1, duration)
    const name = shot.shot_description || `镜头${i + 1}`
    const insertMode = shot.has_narration ? shot.is_role_acting ? 'narration' : 'overwrite' : 'insert'
    const materialExample = shot.shot_path
      ? {
          id: `parse-${i}-${Date.now()}`,
          fileUrl: shot.shot_path,
          fileName: (shot.shot_path && shot.shot_path.split('/').pop()) || `${name}.mp4`,
          prompt: shot.shot_prompt || '',
          thumbnail: '',
          fileSize: 0,
          createDate: '',
          tags: []
        }
      : null
    return {
      id: Date.now() + i + Math.random(),
      name,
      materialExample,
      minDuration: clampedDuration,
      maxDuration: clampedDuration,
      hasPiP: false,
      insertMode
    }
  })
}

function getMaterialExampleVideoUrl(materialExample) {
  if (!materialExample) return null
  if (materialExample.fileUrl) return materialExample.fileUrl
  if (materialExample.fileName && (materialExample.fileName.startsWith('http://') || materialExample.fileName.startsWith('https://'))) {
    return materialExample.fileName
  }
  if (typeof materialExample === 'string' && (materialExample.startsWith('http://') || materialExample.startsWith('https://'))) {
    return materialExample
  }
  return null
}

function handleSelectMaterialExample() {
  selectedVideoId.value = shotForm.value.materialExample ? shotForm.value.materialExample.id : null
  tempSelectedVideo.value = shotForm.value.materialExample ? JSON.parse(JSON.stringify(shotForm.value.materialExample)) : null
  videoSearchKeyword.value = ''
  uploadedVideoUrl.value = ''
  uploadedVideoFileName.value = ''
  videoSelectorTab.value = 'select'
  videoSelectorDialogVisible.value = true
  fetchVideoList()
}

function handleUploadedVideoChange(data) {
  if (data && data.fileName) {
    uploadedVideoUrl.value = data.fileName
    uploadedVideoFileName.value = data.fileName.split('/').pop() || '上传的视频.mp4'
    selectedVideoId.value = null
    tempSelectedVideo.value = null
    ElMessage.success('视频上传成功')
  }
}

function handleVideoTabChange(tabName) {
  if (tabName === 'select') {
    uploadedVideoUrl.value = ''
    uploadedVideoFileName.value = ''
    if (uploadRef.value) uploadRef.value.clearFile()
  } else {
    selectedVideoId.value = null
    tempSelectedVideo.value = null
  }
}

function handleVideoDialogClose() {
  videoSelectorDialogVisible.value = false
  if (videoSelectorTab.value === 'upload') {
    uploadedVideoUrl.value = ''
    uploadedVideoFileName.value = ''
    if (uploadRef.value) uploadRef.value.clearFile()
  }
}

async function fetchVideoList() {
  videoListLoading.value = true
  try {
    await new Promise(resolve => setTimeout(resolve, 300))
    videoList.value = [
      { id: 1, fileName: '产品特写视频.mp4', fileSize: 1024 * 1024 * 50, createDate: '2025-01-20', thumbnail: '', fileUrl: '', tags: ['产品特写', '产品展示'] },
      { id: 2, fileName: '产品全景视频.mp4', fileSize: 1024 * 1024 * 80, createDate: '2025-01-21', thumbnail: '', fileUrl: '', tags: ['产品全景', '产品展示'] },
      { id: 3, fileName: '使用场景视频.mp4', fileSize: 1024 * 1024 * 60, createDate: '2025-01-22', thumbnail: '', fileUrl: '', tags: ['使用场景', '产品展示'] },
      { id: 4, fileName: '人物特写视频.mp4', fileSize: 1024 * 1024 * 70, createDate: '2025-01-23', thumbnail: '', fileUrl: '', tags: ['人物特写', '故事叙述'] },
      { id: 5, fileName: '操作演示视频.mp4', fileSize: 1024 * 1024 * 90, createDate: '2025-01-24', thumbnail: '', fileUrl: '', tags: ['操作演示', '产品展示'] }
    ]
  } catch (error) {
    videoList.value = []
    ElMessage.error('获取视频列表失败')
  } finally {
    videoListLoading.value = false
  }
}

function handleSelectVideo(video) {
  selectedVideoId.value = video.id
  tempSelectedVideo.value = JSON.parse(JSON.stringify(video))
}

function handleConfirmVideoSelection() {
  if (uploadedVideoUrl.value) {
    const uploadedVideo = {
      id: Date.now(),
      fileName: uploadedVideoFileName.value || uploadedVideoUrl.value.split('/').pop() || '上传的视频.mp4',
      fileUrl: uploadedVideoUrl.value,
      thumbnail: '',
      fileSize: 0,
      createDate: new Date().toISOString().split('T')[0],
      tags: []
    }
    shotForm.value.materialExample = uploadedVideo
    videoSelectorDialogVisible.value = false
    ElMessage.success('视频选择成功')
    return
  }
  if (tempSelectedVideo.value) {
    shotForm.value.materialExample = tempSelectedVideo.value
    videoSelectorDialogVisible.value = false
    ElMessage.success('视频选择成功')
  } else {
    ElMessage.warning('请选择一个视频或上传新视频')
  }
}

function handleConfirmShot() {
  if (!shotForm.value.name) {
    ElMessage.warning('请输入镜头名称')
    return
  }
  if (shotForm.value.minDuration > shotForm.value.maxDuration) {
    ElMessage.warning('最短时长不能大于最大时长')
    return
  }

  // 处理 materialExample，将 prompt 保存到其中
  let materialExample = null
  if (shotForm.value.materialExample) {
    materialExample = JSON.parse(JSON.stringify(shotForm.value.materialExample))
    materialExample.prompt = shotForm.value.prompt || ''
  } else if (shotForm.value.prompt) {
    // 如果没有选择素材但填写了提示词，创建一个空的 materialExample 对象来保存提示词
    materialExample = {
      id: null,
      fileName: '',
      fileUrl: '',
      thumbnail: '',
      fileSize: 0,
      createDate: '',
      tags: [],
      prompt: shotForm.value.prompt
    }
  }

  const shotData = {
    id: editingShotIndex.value >= 0 ? templateForm.value.shots[editingShotIndex.value].id : Date.now() + Math.random(),
    name: shotForm.value.name,
    materialExample: materialExample,
    minDuration: shotForm.value.minDuration,
    maxDuration: shotForm.value.maxDuration,
    hasPiP: shotForm.value.hasPiP,
    insertMode: shotForm.value.insertMode
  }
  if (editingShotIndex.value >= 0) {
    templateForm.value.shots.splice(editingShotIndex.value, 1, shotData)
    editingShotIndex.value = -1
    shotDialogVisible.value = false
    ElMessage.success('镜头修改成功')
  } else {
    templateForm.value.shots.push(shotData)
    shotDialogVisible.value = false
    ElMessage.success('镜头添加成功')
  }
}

function handleRemoveShot(index) {
  templateForm.value.shots.splice(index, 1)
}

// 封面上传相关方法
function handleCoverImageChange(data) {
  if (data && data.fileName) {
    templateForm.value.coverImage = data.fileName
    ElMessage.success('封面上传成功')
  }
}

function handleClearCoverImage() {
  templateForm.value.coverImage = null
  if (coverUploadRef.value && coverUploadRef.value.clearFile) {
    coverUploadRef.value.clearFile()
  }
}

function initSortable() {
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }
  if (!shotsListRef.value) return
  sortableInstance = Sortable.create(shotsListRef.value, {
    handle: '.shot-drag-handle',
    animation: 200,
    ghostClass: 'shot-ghost',
    chosenClass: 'shot-chosen',
    dragClass: 'shot-drag',
    onEnd: (evt) => {
      const { oldIndex, newIndex } = evt
      if (oldIndex !== newIndex) {
        const movedItem = templateForm.value.shots.splice(oldIndex, 1)[0]
        templateForm.value.shots.splice(newIndex, 0, movedItem)
        ElMessage.success('镜头顺序已更新')
      }
    }
  })
}

// 计算每行可以显示多少个镜头
function calculateShotsPerRow() {
  const shotItemWidth = 158
  const gap = 4
  const minShots = 3
  const maxShots = 12
  const sidebarEl = document.querySelector('.sidebar')
  const sidebarWidth = sidebarEl ? sidebarEl.offsetWidth : 270
  const containerWidth = window.innerWidth - sidebarWidth - 560
  const calculated = Math.floor((containerWidth + gap) / (shotItemWidth + gap))
  shotsPerRow.value = Math.max(minShots, Math.min(maxShots, calculated))
}

// 监听窗口大小变化
function handleResize() {
  calculateShotsPerRow()
}

// 保留 elephant-web 的折叠侧栏适配。
let sidebarObserver = null
function initSidebarObserver() {
  const sidebarEl = document.querySelector('.sidebar')
  if (!sidebarEl) return
  sidebarObserver = new MutationObserver(() => {
    setTimeout(calculateShotsPerRow, 350)
  })
  sidebarObserver.observe(sidebarEl, { attributes: true, attributeFilter: ['class'] })
}

onMounted(() => {
  if (!isLoggedIn()) {
    if (!props.embedded) {
      redirectToLogin()
    }
    return
  }
  loadTemplateList()
  calculateShotsPerRow()
  window.addEventListener('resize', handleResize)
  initSidebarObserver()
})

onBeforeUnmount(() => {
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }
  window.removeEventListener('resize', handleResize)
  if (sidebarObserver) {
    sidebarObserver.disconnect()
    sidebarObserver = null
  }
})
</script>

<style scoped>
.template-mng {
  padding: 30px 40px;
  height: 100%;
  width: 100%;
  overflow: auto;
  background: #fff;
  box-sizing: border-box;
}

.template-mng.is-embedded {
  min-height: 0;
  height: 100%;
  overflow: auto;
  border: 1px solid var(--border-light, #E5E7EB);
  border-radius: var(--radius-lg, 16px);
  box-shadow: none;
}

.template-mng.is-embedded .template-list {
  max-height: none;
  overflow: visible;
}

.template-mng::-webkit-scrollbar {
  width: 6px;
}
.template-mng::-webkit-scrollbar-track {
  background: transparent;
}
.template-mng::-webkit-scrollbar-thumb {
  background: #E5E7EB;
  border-radius: 3px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #E5E7EB;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary, #1F2937);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.template-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: calc(100vh - 190px);
  overflow-y: auto;
  padding-right: 4px;
  padding-top: 4px;
}

.template-list::-webkit-scrollbar {
  width: 6px;
}
.template-list::-webkit-scrollbar-track {
  background: transparent;
}
.template-list::-webkit-scrollbar-thumb {
  background: #E5E7EB;
  border-radius: 3px;
}

.template-item {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: default;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  /* 保险：让标题渐变底色即使扩展到 padding 外侧也不会溢出圆角 */
  overflow: hidden;
  position: relative;
}

.template-item:hover {
  border-color: var(--accent-blue, #4F46E5);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.template-header {
  display: flex;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.95);
  position: relative;
}

.template-header::before {
  content: '';
  position: absolute;
  /* 抵消 template-item 的 padding: 16px 20px，让渐变贴齐卡片上/左右边缘 */
  left: -20px;
  right: -20px;
  top: -16px;
  bottom: 0;
  /* 轻量渐变底色：用于标题层高级感，不抢内容注意力 */
  background:
    radial-gradient(520px 140px at 18% 0%, rgba(79, 70, 229, 0.18), transparent 60%),
    radial-gradient(420px 140px at 88% 10%, rgba(147, 51, 234, 0.14), transparent 55%),
    linear-gradient(135deg, rgba(79, 70, 229, 0.10) 0%, rgba(147, 51, 234, 0.08) 55%, rgba(99, 102, 241, 0.06) 100%);
  /* 左下/右下不要圆角，只让上边圆一点 */
  border-radius: 16px 16px 0 0;
  opacity: 1;
  z-index: 0;
  pointer-events: none;
}

.template-header > * {
  position: relative;
  z-index: 1;
}

.template-cover-section {
  flex-shrink: 0;
}

.template-cover-image {
  width: 120px;
  height: 68px;
  border-radius: 6px;
  overflow: hidden;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
}

.template-cover-image .cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  font-size: 12px;
  gap: 4px;
}

.template-header-right {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.template-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.template-name {
  margin: 0;
  color: var(--text-primary, #1F2937);
  font-size: 16px;
  font-weight: 600;
}

.template-actions {
  display: flex;
  gap: 8px;
}

.template-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.template-shots-section {
  margin-top: 8px;
}

.shots-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;
}

.shot-view-item-inline {
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  width: 158px;
  height: 100px;
  flex-shrink: 0;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shot-item-header-inline {
  padding-bottom: 6px;
  border-bottom: 1px solid #E5E7EB;
  color: var(--text-primary, #1F2937);
  font-weight: 600;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shot-info-list {
  display: flex;
  flex-direction: row;
  gap: 6px;
}

.material-example-container {
  width: 68px;
  height: 68px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: #F3F4F6;
}

.material-example-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.material-example-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #F3F4F6;
}

.material-example-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
}

.shot-info-item {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.empty-template-list {
  text-align: center;
  padding: 40px;
  color: var(--text-secondary, #6B7280);
}

.shot-more-hint {
  background: #F9FAFB;
  border: 1px dashed #D1D5DB;
  border-radius: 8px;
  width: 158px;
  height: 100px;
  flex-shrink: 0;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: var(--accent-blue, #4F46E5);
  transition: all 0.3s ease;
}

.shot-more-hint:hover {
  border-color: var(--accent-blue, #4F46E5);
  background: rgba(79, 70, 229, 0.05);
}

.shot-more-hint span {
  font-size: 13px;
  font-weight: 500;
}

/* 模板编辑对话框 */
.template-edit-dialog :deep(.el-dialog) {
  background: #fff;
  border: 1px solid #E5E7EB;
}

.template-edit-dialog :deep(.el-dialog__header) {
  background: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
}

.template-edit-dialog :deep(.el-dialog__title) {
  color: var(--text-primary, #1F2937);
}

.template-edit-dialog :deep(.el-dialog__body) {
  background: #fff;
  max-height: 600px;
  overflow-y: auto;
}

.template-edit-dialog :deep(.el-dialog__footer) {
  background: #F9FAFB;
  border-top: 1px solid #E5E7EB;
}

.template-edit-dialog :deep(.el-form-item__label) {
  color: var(--text-primary, #1F2937);
}

.template-edit-dialog :deep(.el-input__wrapper),
.template-edit-dialog :deep(.el-textarea__inner) {
  background-color: #fff;
  border-color: #E5E7EB;
  color: var(--text-primary, #1F2937);
}

.shots-edit-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.add-shot-btn {
  width: fit-content;
  align-self: flex-start;
}
.power-text {
  margin-left: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.shots-edit-list {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  height: 400px;
  gap: 8px;
  width: 100%;
  overflow-y: auto;
}

.shots-edit-list::-webkit-scrollbar-thumb {
  background: #E5E7EB;
  border-radius: 4px;
}

.shot-view-item {
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  width: 188px;
  height: 120px;
  flex-shrink: 0;
  padding: 2px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: default;
}

.shot-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 2px;
  border-bottom: 1px solid #E5E7EB;
  color: var(--text-primary, #1F2937);
  font-weight: 600;
  font-size: 13px;
  gap: 1px;
}

.shot-name-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shot-drag-handle {
  cursor: move;
  cursor: grab;
  color: #9CA3AF;
  display: flex;
  align-items: center;
  padding: 4px;
}

.shot-drag-handle:hover {
  color: var(--accent-blue, #4F46E5);
}

.shot-ghost {
  opacity: 0.5;
  background: rgba(79, 70, 229, 0.05);
  border-color: var(--accent-blue, #4F46E5);
}

.shot-chosen {
  border-color: var(--accent-blue, #4F46E5);
  box-shadow: 0 0 8px rgba(79, 70, 229, 0.2);
}

.shot-drag {
  opacity: 0.8;
  transform: scale(1.05);
}

.empty-shots-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 40px;
  color: var(--text-secondary, #6B7280);
  height: 70%;
  border: 2px dashed #E5E7EB;
  border-radius: 8px;
}

/* 镜头编辑对话框 */
.shot-edit-dialog :deep(.el-dialog) {
  background: #fff;
  border: 1px solid #E5E7EB;
}

.shot-edit-dialog :deep(.el-dialog__header),
.shot-edit-dialog :deep(.el-dialog__footer) {
  background: #F9FAFB;
  border-color: #E5E7EB;
}

.shot-edit-dialog :deep(.el-dialog__title) {
  color: var(--text-primary, #1F2937);
}

.shot-edit-dialog :deep(.el-dialog__body) {
  background: #fff;
}

.shot-edit-dialog :deep(.el-form-item__label) {
  color: var(--text-primary, #1F2937);
}

.shot-edit-dialog :deep(.el-input__wrapper) {
  background-color: #fff;
  border-color: #E5E7EB;
  color: var(--text-primary, #1F2937);
}

.material-example-selector {
  width: 150px;
  height: 150px;
}

.material-example-preview {
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #F9FAFB;
  border: 2px dashed #D1D5DB;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.material-example-preview:hover {
  border-color: var(--accent-blue, #4F46E5);
  background: rgba(79, 70, 229, 0.03);
}

.material-example-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
}

.material-example-thumbnail {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.material-example-thumbnail .thumbnail-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

.material-example-thumbnail .thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.material-example-thumbnail .thumbnail-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F3F4F6;
  color: #9CA3AF;
}

.material-example-thumbnail .thumbnail-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: #fff;
}

.material-example-preview:hover .thumbnail-overlay {
  opacity: 1;
}

.clear-material-btn {
  position: absolute;
  top: 4px;
  right: 4px;
}

/* 视频选择对话框 */
.video-selector-dialog :deep(.el-dialog) {
  background: #fff;
  border: 1px solid #E5E7EB;
}

.video-selector-dialog :deep(.el-dialog__header),
.video-selector-dialog :deep(.el-dialog__footer) {
  background: #F9FAFB;
  border-color: #E5E7EB;
}

.video-selector-dialog :deep(.el-dialog__title) {
  color: var(--text-primary, #1F2937);
}

.video-search-section {
  margin-bottom: 12px;
}

.video-loading,
.empty-video-list {
  padding: 24px;
  text-align: center;
  color: var(--text-secondary, #6B7280);
}

.video-selector-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.video-selector-item {
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.video-selector-item:hover,
.video-selector-item-selected {
  border-color: var(--accent-blue, #4F46E5);
  box-shadow: 0 0 8px rgba(79, 70, 229, 0.15);
}

.video-selector-thumbnail {
  aspect-ratio: 16/9;
  background: #F3F4F6;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.thumbnail-icon {
  color: #666;
}

.video-selector-thumbnail .thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.8;
}

.video-selector-info {
  padding: 8px;
}

.video-selector-name {
  font-size: 13px;
  color: var(--text-primary, #1F2937);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-selector-tags {
  margin-top: 4px;
}

.video-upload-section {
  padding: 16px 0;
}

.video-upload-section {
  padding: 16px 0;
}

/* 封面上传预览样式 */
.cover-image-preview-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.cover-image-preview-container .cover-preview-image {
  width: 200px;
  height: 112px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #333;
}

/* 解析视频弹窗 */
.parse-video-dialog :deep(.el-dialog) {
  background: #1e1e1e;
  border: 1px solid #333;
}

.parse-video-dialog :deep(.el-dialog__header),
.parse-video-dialog :deep(.el-dialog__footer) {
  background: #262626;
  border-color: #333;
}

.parse-video-dialog :deep(.el-dialog__title) {
  color: #fff;
}

.parse-video-dialog :deep(.el-dialog__body) {
  background: #1e1e1e;
}

.video-preview-tab {
  width: 100%;
}

.video-preview-tab .video-preview-relative {
  position: relative;
  width: 100%;
}
</style>
